import axios from "axios";

import { useUserStore } from "@/stores/user.ts";
import type { LoginUserInfo } from "@/stores/user.ts";

const baseURL = import.meta.env.VITE_API_BASE || "/api/";

// 创建一个 axios 实例
const service = axios.create({
  baseURL: baseURL, // 所有的请求地址前缀部分
  timeout: 60000, // 请求超时时间毫秒
  withCredentials: true, // 异步请求携带cookie
  headers: {
    // 设置后端需要的传参类型
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// 请求拦截器 - no longer need to set X-Token header as we use cookies
service.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent by the browser
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器
service.interceptors.response.use(
  function (response) {
    return response.data;
  },
  async function (error) {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Attempt to refresh token
        const refreshResponse = await service.post('/user/login/refresh');
        // Update user store with new user info
        const userStore = useUserStore();
        // 该接口返回用户信息本体（后端约定 code === 0）
        userStore.loadResponse(refreshResponse as unknown as LoginUserInfo);
        return service(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear user store and redirect to login
        const userStore = useUserStore();
        userStore.clear();
        // You might want to redirect to login page here
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

declare module "axios" {
  interface AxiosInstance {
    /**
     * 响应拦截器已经把 `response.data` 拆出来了（见上方 interceptors.response），
     * 所以运行时拿到的是接口返回体本身，而不是 AxiosResponse。
     *
     * TODO(类型化接口边界)：这里最终应当是 `Promise<CodeResult<T>>`。
     * 但本项目的接口函数基本没有声明返回类型，调用点也普遍依赖 `res.data`
     * 直接取值；一旦把这里收紧成 `unknown`，约 200 个调用点会立刻报
     * "'res.data' is of type 'unknown'"。因此先保留 any 语义，
     * 等逐个补齐接口返回类型后再收紧（见 types/request.ts 的 CodeResult）。
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config: import("axios").AxiosRequestConfig): Promise<any>;
  }
}

export default service;
